import { useQuery, useMutation } from "convex/react";
import { api } from "../../api";
import { toast } from "@/hooks/use-toast";

interface DeviceBrowserPolicy {
  _id: string;
  deviceId: string;
  blueJayRules?: {
    blockVideos?: boolean;
    nsfwLevel?: number;
    skinToneColoring?: boolean;
    accuracy?: number;
  };
}

export function useDeviceBrowserPolicies(deviceId: string) {
  // Fetch device details
  const deviceDetails = useQuery(api.devices.get.details, { device: deviceId as any });
  
  // Mutation to update device policies
  const updateDevicePoliciesMutation = useMutation(api.devices.policies.update);
  
  const browserPolicy: DeviceBrowserPolicy | null = deviceDetails ? {
    _id: deviceDetails.device.deviceConfig,
    deviceId,
    blueJayRules: {} // Device details doesn't seem to include blueJayRules directly
  } : null;
  
  const updateBrowserPolicy = async (updates: {
    blockVideos?: boolean;
    nsfwLevel?: number;
    skinToneColoring?: boolean;
    accuracy?: number;
  }) => {
    if (!browserPolicy) {
      toast({
        title: "Error",
        description: "Device configuration not loaded",
        variant: "destructive",
      });
      return;
    }

    try {
      const currentBlueJayRules = browserPolicy.blueJayRules || {};
      const updatedBlueJayRules = {
        ...currentBlueJayRules,
        ...updates
      };

      await updateDevicePoliciesMutation({
        device: deviceId as any,
        blueJayRules: updatedBlueJayRules
      });

      toast({
        title: "Success",
        description: "Browser policy updated successfully",
      });
    } catch (error) {
      console.error("Failed to update browser policy:", error);
      toast({
        title: "Error",
        description: "Failed to update browser policy",
        variant: "destructive",
      });
    }
  };

  return {
    browserPolicy,
    isLoading: deviceDetails === undefined,
    updateBrowserPolicy,
  };
}